import 'react-app-polyfill/stable';
import { createRoot } from 'react-dom/client';
import ReportBuilderApp from './ReportBuilderApp';
import {
    checkForEmbeddedLaunch,
    IAppConfig,
    IAppStartupConfig
} from './utils/appUtilsAndConstants';
import * as serviceWorker from './serviceWorker';
// import WebFont from 'webfontloader';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { fetchSessionState } from './redux/builderStore';
import { isNullOrUndefined, lowerKeyParams } from './utils/object';
// import sha256 from 'crypto-js/sha256';
// import Base64 from 'crypto-js/enc-base64';
import salt64 from './salt.json';
import { MESSAGE_BUNDLE, getReactIntlHtmlFuncs } from './utils/localization';
// Vanilla config to update defaults below - can then be overridden in turn by configs in /public folder
import appConfig from './app.config.json';
// Calcite basics
import '@esri/calcite-components/dist/calcite/calcite.css';
import { setAssetPath } from '@esri/calcite-components';
// ArcGIS basics
import esriConfig from '@arcgis/core/config.js';
//import esriId from '@arcgis/core/identity/IdentityManager';
import '@arcgis/core/assets/esri/themes/light/main.css';
import '@esri/calcite-components/dist/components/calcite-alert';
import { CalciteAlert } from '@esri/calcite-components-react';

// Local assets
const basePath = `${window.location.protocol}//${window.location.hostname}${
    window.location.port !== '80' && window.location.port !== '443'
        ? `:${window.location.port}`
        : ''
}/`;
//esriConfig.assetsPath = `${basePath}assets`; // This should only be done for local deployments, see below
setAssetPath(basePath);
// CDN hosted assets
//setAssetPath('https://unpkg.com/@esri/calcite-components/dist/calcite/assets');

let defaults: IAppStartupConfig = {
    //basename: '/',
    container: 'reportBuilderRoot',
    appId: 'a0905cde786f4b04a3220b44151845c5',
    appAuthId: 'Z4nkfRignhCExg0m',
    appHost: 'https://www.reports.esriuk.com',
    appHelp: 'https://help.reports.esriuk.com/report-builder/',
    // These are null values - need to be replaced for build
    staticHost: '',
    publisherUrl: '',
    // Token for accessing ArcGIS Online/Portal
    token: '',
    tokenExpiry: -1,
    // Where is the ArcGIS Portal instance?
    portal: {
        url: 'https://www.arcgis.com/sharing/rest/',
        // And what is it - Online or (Portal or Enterprise)
        type: 'online',
        home: 'https://www.arcgis.com/'
    },
    // Other InstantAtlas ArcGIS apps...
    appLinks: {
        dataCatalog: {
            appId: '59eff52adbfb47c9a24d16b46fe6d068',
            url: 'https://hub.instantatlas.com/'
        }
    },
    fontFamily: 'Avenir Next',
    fontSize: 16,
    // Called when the app is ready and has rendered a page.
    onReady: () => {
        //console.debug('\n\nAPP READY - 1st RENDER');
    },
    // Called when an error occurs in the ArcGIS Online communication.
    onError: (err) => {
        console.log(err);
    },
    user: null,
    locale: 'en',
    messages: {},
    routerMode: 'browser', // Makes embedding easier if we have the ability to use HashRouter as well
    configPathPattern: '{appHost}/app-config-{domainKey}.json',
    featureSwitches: {}
};

const launchAppWithSettings = (settings: IAppStartupConfig, components: JSX.Element[]) => {
    const appHomeUrl =
            settings.routerMode !== undefined &&
            settings.routerMode === 'hash' &&
            !/^(.*)#$/.test(settings.appHost)
                ? `${settings.appHost}`
                : settings.appHost,
        reactAppNode = document.querySelector(
            `#${settings.container}`.replace('##', '#').replace('#.', '.')
        ),
        overriddenAppSettings = settings.overrides !== undefined ? { ...settings.overrides } : {},
        {
            appId,
            appAuthId,
            appHelp,
            routerMode,
            portal,
            publisherUrl,
            staticHost,
            token,
            tokenExpiry,
            user,
            appLinks,
            onError,
            basename,
            error,
            className,
            location,
            embedded,
            onReady,
            featureSwitches
        } = settings,
        afterRenderReady = (e: any) => {
            if (onReady !== undefined) onReady(reactAppNode, { ...settings }, e);
        };
    if (appHomeUrl !== undefined && appHomeUrl !== null && /^https:\/\/.*/.test(appHomeUrl)) {
        (window as any)['__webpack_public_path__'] = appHomeUrl;
        if ((window as any)['__webpack_require__'] !== undefined)
            (window as any)['__webpack_require__'].p = appHomeUrl;
        setAssetPath(appHomeUrl);
        if (
            portal.type === 'portal' &&
            !/^https:\/\/(localhost|.*\.reports\.esriuk\.com)/.test(appHomeUrl)
        )
            esriConfig.assetsPath = `${appHomeUrl.replace(/(.*)\/$/, '$1')}/assets`;
    }
    const root = createRoot(reactAppNode!); // createRoot(container!) if you use TypeScript
    root.render(
        <IntlProvider locale={settings.locale || 'en'} messages={settings.messages}>
            <ReportBuilderApp
                onReady={undefined}
                queryString={undefined}
                onRender={afterRenderReady}
                id={appId}
                authId={appAuthId}
                homeUrl={appHomeUrl}
                helpUrl={appHelp}
                routerMode={routerMode}
                portalUrl={portal.url}
                portalType={portal.type}
                portalHome={portal.home}
                publisherUrl={publisherUrl}
                staticUrl={staticHost}
                token={token}
                tokenExpiry={tokenExpiry}
                user={user}
                appLinks={appLinks}
                onError={onError}
                basename={basename}
                inErrorState={error !== undefined && error !== null}
                error={error}
                className={className}
                extraComponents={components}
                location={location}
                windowMode={embedded !== undefined && embedded === true ? 'embedded' : 'standalone'}
                overrides={overriddenAppSettings}
                featureSwitches={featureSwitches}
            />
        </IntlProvider>
    );
    document.body.classList.add(`host-${(window.location.hostname || 'localhost').split('.')[0]}`);
};

const isValidString = (str: string | undefined | null) => {
    return str !== undefined && str !== null && str !== '';
};

const launchWithConfig = async (hardOptions: IAppConfig = {}, components: JSX.Element[] = []) => {
    // Standard ArcGIS naming...
    const host =
            hardOptions.host !== undefined && hardOptions.host !== null
                ? hardOptions.host
                : isValidString(import.meta.env.BASE_URL)
                  ? import.meta.env.BASE_URL
                  : '/',
        configPath = host.replace(/\/$/, ''),
        activeAppPath = /^http|https:\/\//.test(configPath)
            ? configPath
            : `${window.location.protocol}//${window.location.hostname}${
                  window.location.port !== '' &&
                  window.location.port !== '80' &&
                  window.location.port !== '443'
                      ? `:${window.location.port}`
                      : ''
              }`;
    try {
        // Override default settings with passed in options.
        let settings = Object.assign(Object.assign(defaults, appConfig), hardOptions);
        let dynamicAppConfig =
            !/^https:\/\/:reports\.instantatlas\.com|www\.reports\.esriuk\.com/.test(activeAppPath)
                ? await fetch(`${configPath}/app-config.json`).then((cfgRsp) => {
                      return cfgRsp.json();
                  })
                : {};
        // Special case - multi-site within a known domain
        const appDomainName = activeAppPath.match(
                /^https:\/\/([a-zA-Z0-9-]+)\.reports\.esriuk\.com|reports-([a-zA-Z0-9-]+)\.instantatlas\.com|https:\/\/(localhost|reports\.instantatlas\.local|local-development\.alt\.esriuk\.com)(:[0-9]{4})?/
            ),
            alternateDomain =
                dynamicAppConfig.appHost !== undefined &&
                dynamicAppConfig.appHost !== activeAppPath;
        if (
            alternateDomain &&
            appDomainName !== null &&
            Array.isArray(appDomainName) &&
            appDomainName.length > 1
        ) {
            try {
                const tempConfig = {
                        ...settings,
                        ...dynamicAppConfig
                    },
                    subDomainName =
                        appDomainName[1] !== undefined
                            ? appDomainName[1]
                            : appDomainName[2] !== undefined
                              ? appDomainName[2]
                              : appDomainName[3] !== undefined &&
                                  (appDomainName[3] === 'localhost' ||
                                      appDomainName[3] === 'local-development.alt.esriuk.com')
                                ? appDomainName[3]
                                : 'never-ever',
                    overridesPath = (
                        tempConfig.configPathPattern || '{appHost}/app-config-{domainKey}.json'
                    )
                        .replace('{appHost}', configPath)
                        .replace('{subDomain}', subDomainName)
                        .replace('{domainKey}', subDomainName.replace(/[^0-9a-zA-Z]/g, '-')),
                    overridesAppConfig =
                        subDomainName !== 'never-ever'
                            ? await fetch(overridesPath).then((cfgRsp) => {
                                  if (!cfgRsp.ok) return {}; //throw new Error(`${cfgRsp.status} ${cfgRsp.statusText}`); // Silent - it's allowed to be a 404 etc.
                                  return cfgRsp.json();
                              })
                            : {};
                dynamicAppConfig = {
                    ...dynamicAppConfig,
                    ...overridesAppConfig
                };
            } catch (overrideEx) {
                console.debug(
                    `Cannot override settings with customer-specific ones for key ${
                        appDomainName[1] || appDomainName[2]
                    }: ${overrideEx}`
                );
            }
        }
        // Clean up a few "any" settings - simple on/off feature flags as "--flag1,--flag2" (similar to URL query string)
        if (
            dynamicAppConfig.featureFlags &&
            typeof dynamicAppConfig.featureFlags === 'string' &&
            dynamicAppConfig.featureFlags !== ''
        ) {
            const flags = dynamicAppConfig.featureFlags.split(',');
            dynamicAppConfig.featureSwitches = {};
            for (const f of flags) {
                dynamicAppConfig.featureSwitches[f.substring(2)] = 'true';
            }
        }
        settings = {
            ...settings,
            ...dynamicAppConfig,
            ...hardOptions
        };
        // Special case - running at root URL...
        if (host === '/' && settings.appHost === '/')
            settings.appHost = `${window.location.protocol}//${window.location.hostname}${
                window.location.port !== '80' && window.location.port !== '443'
                    ? `:${window.location.port}`
                    : ''
            }`;
        if (hardOptions.host !== undefined) {
            settings.appHost = hardOptions.host;
        }
        //checkLicence(activeAppPath, configPath); // Redundant just now
        // If running standalone, pull in the arguments from the address bar.
        const qs = lowerKeyParams(new URLSearchParams(window.location.search)),
            hashArgs = lowerKeyParams(
                new URLSearchParams(
                    !isNullOrUndefined(window.location.hash)
                        ? window.location.hash.substring(1)
                        : ''
                )
            ),
            featureSwitches: { [id: string]: string } = {};
        for (let k of Object.keys(qs)) {
            if (k.indexOf('--') === 0) featureSwitches[k.substring(2)] = qs[k];
        }
        settings.featureSwitches = {
            ...(settings.featureSwitches || {}),
            ...featureSwitches
        };
        // Auth back-door for IA team
        if (qs['app-auth-id'] !== undefined) settings.appAuthId = qs['app-auth-id'];
        // Token?
        // try {
        //     const tc = await esriId.checkSignInStatus(settings.portal.url);
        //     settings.token = tc.token;
        //     settings.tokenExpiry = tc.expires;
        // } catch (tcex) {
        //     settings.token = null;
        // }
        if (!isValidString(settings.token) && isValidString(qs.token)) {
            settings.token = qs.token;
            if (isValidString(qs.expires) && /^[0-9]+$/.test(qs.expires)) {
                settings.tokenExpiry = Number.parseInt(qs.expires);
            }
        } else if (!isValidString(settings.token) && isValidString(hashArgs.access_token)) {
            // This should be handled by IdentityManager
            // settings.token = hashArgs.access_token;
            // settings.tokenExpiry = new Date().getTime() + 1000 * hashArgs.expires_in; // seconds to milliseconds
            // window.location.hash = ''; // Extracted the token - no longer need the hash
        }
        const compositeArgs = {
            ...hashArgs,
            ...qs
        };
        if (!isNullOrUndefined(compositeArgs.error)) {
            if (
                compositeArgs.error === 'invalid_request' ||
                compositeArgs.error === 'access_denied'
            ) {
                settings.token = null;
                settings.tokenExpiry = new Date().getTime() - 1000; // seconds to milliseconds - the past
            }
            if (compositeArgs.error !== 'access_denied') {
                components.push(
                    <CalciteAlert
                        icon="circle-disallowed"
                        open={true}
                        autoClose={true}
                        autoCloseDuration="slow"
                        kind="danger"
                        placement="top"
                        className="alert alert-danger ia-error-block"
                    >
                        <div slot="title">
                            <FormattedMessage
                                id="home.authFail.title"
                                defaultMessage="Access denied"
                            />
                        </div>
                        <div slot="message">
                            <FormattedMessage
                                id="home.authFail.message"
                                defaultMessage={`
                                <p>You do not have permission to access <strong>Report Builder for ArcGIS</strong> in <em>edit mode</em>.
                                If you think you should have access please contact your ArcGIS Online organization's administrator and check licence allocation or 
                                check your subscription status via <a>{link};ArcGIS Marketplace</a>. The full error message is shown below.</p>
                                <p>{message}</p>
                                `}
                                values={{
                                    ...getReactIntlHtmlFuncs(),
                                    message: (
                                        <strong>
                                            {compositeArgs.error_description ?? compositeArgs.error}
                                        </strong>
                                    ),
                                    link: 'https://marketplace.arcgis.com/listing.html?id=a0905cde786f4b04a3220b44151845c5'
                                }}
                            />
                        </div>
                    </CalciteAlert>
                );
            }
        }
        // Persisted token? May not have expired yet...
        const storedSettings = fetchSessionState('reportBuilderState');
        if (
            !isValidString(settings.token) &&
            !isNullOrUndefined(storedSettings) &&
            !isNullOrUndefined(storedSettings.appSettings) &&
            isValidString(storedSettings.appSettings.token)
        ) {
            settings.token = storedSettings.appSettings.token;
            settings.tokenExpiry = storedSettings.appSettings.tokenExpiry;
            if (settings.tokenExpiry !== undefined && settings.tokenExpiry < new Date().getTime()) {
                // Expired - clear it, force a re-auth
                settings.token = '';
                settings.tokenExpiry = -1;
            }
        }
        // Locale
        if (!isNullOrUndefined(qs.locale)) {
            settings.locale = qs.locale;
        }
        settings.messages = MESSAGE_BUNDLE[settings.locale]
            ? MESSAGE_BUNDLE[settings.locale]
            : settings.locale.split('-').length > 1 && MESSAGE_BUNDLE[settings.locale.split('-')[0]]
              ? MESSAGE_BUNDLE[settings.locale.split('-')[0]]
              : {};
        // Development overrides...
        if (import.meta.env.DEV === true && !isNullOrUndefined(qs.portalType)) {
            settings.portal.type = qs.portalType;
        }
        // Online - quick check that the portal is available
        const appIsOnline =
            typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
                ? navigator.onLine
                : true;
        if (appIsOnline) {
            if (settings.portal === undefined || settings.portal.url === undefined) {
                throw new Error('Configuration invalid - no suitable portal URL');
            }
            const portalBasics = await fetch(
                `${settings.portal.url.replace(/^\/(.*)\/$/, '$1')}/?f=json`
            ).then((portalRsp) => {
                return portalRsp.json();
            });
            if (portalBasics === undefined || portalBasics.currentVersion === undefined)
                throw new Error(`Cannot connect to portal on URL ${settings.portal.url}`);
        }
        if (hardOptions.embedded !== undefined && hardOptions.embedded === true) {
            // these are in index.html but if we are not using that need to get them explicitly...
            // WebFont.load({
            //     custom: {
            //         families: ['Avenir-light', 'Font Awesome 5 Free'],
            //         urls: [
            //             `${configPath}/static/css/esri-fonts-modern.css`,
            //             `${configPath}/static/css/fontawesome-all.min.css`
            //         ]
            //     }
            // });
        }
        //}
        // Unregistered errors
        if (!isValidString(settings.appId) || settings.appId.substring(0, 1) === '<') {
            const errSettings = Object.assign(defaults, {
                error: {
                    code: 503,
                    message:
                        'App "config.json" invalid - update required - see https://help.reports.esriuk.com/report-builder/'
                }
            });
            launchAppWithSettings(errSettings, components);
        }
        // Devious - sneak in basename here if it is not explicitly set...
        //if (isNullOrUndefined(options.basename) && !isNullOrUndefined(configPath) && (configPath !== '/')) settings.basename = `${configPath}/`;
        // Auth or not auth? Launch...
        if (settings.token !== undefined && settings.token !== null && settings.token.length > 10) {
            const hardSettings = {
                    ...settings // Sigh, JavaScript and threading and objects and all sorts of nightmares...
                },
                portalSelf = await fetch(
                    `${settings.portal.url.replace(/^\/(.*)\/$/, '$1')}/portals/self?f=json&token=${
                        settings.token
                    }`
                ).then((portalRsp) => {
                    return portalRsp.json();
                });
            // User fail - just fail silently and leave them at sign in
            if (portalSelf.error !== undefined) {
                hardSettings.token = null;
                hardSettings.user = null;
            } else {
                hardSettings.user = portalSelf.user;
                if (
                    !isNullOrUndefined(portalSelf.urlKey) &&
                    !isNullOrUndefined(portalSelf.customBaseUrl)
                )
                    settings.portal.url =
                        'https://' +
                        portalSelf.urlKey +
                        '.' +
                        portalSelf.customBaseUrl +
                        '/sharing/rest/';
            }
            launchAppWithSettings(hardSettings, components);
        } else {
            launchAppWithSettings(settings, components);
        }
    } catch (err: any) {
        console.error(err.message); // DEBUG
        // Override default settings with passed in options.
        let settings = Object.assign(defaults, {
            error: err
        });
        launchAppWithSettings(settings, components);
    }
};

// Default is to launch the app "proper" but this can be overridden if the scripts were injected
if (
    !checkForEmbeddedLaunch(launchWithConfig, (injectionHost) => {
        if (injectionHost !== null) setAssetPath(injectionHost);
    })
) {
    // Not been called as part of an embedded script, so load normally...
    // Unless it's a legacy path that we want to throw them out and load an equivalent
    const hardPaths: { [id: string]: string } = {
        '/report/aggregator': '/custom-area-reporter/'
    };
    if (hardPaths[window.location.pathname] !== undefined) {
        const url = hardPaths[window.location.pathname],
            qs = new URLSearchParams(
                url.indexOf('?') < 0 &&
                    window.location.search !== undefined &&
                    window.location.search !== null
                    ? window.location.search
                    : ''
            ),
            // deepcode ignore OR: is a safe replacement, we have already parsed the query string and is handled elsewhere anyway
            target = `${url}?${qs.toString()}`;
        window.location.replace(target);
    }
    // Detect some well known paths to get the correct config from root
    else if (
        window.location.pathname !== undefined &&
        window.location.pathname.search(/[/](edit|view)[-]report[/]/) >= 0
    ) {
        const cut = window.location.pathname.search(
            /[/](edit|view|preview)[-](report|publication)[/]/
        );
        launchWithConfig({
            host: window.location.pathname.substring(0, cut + 1)
        });
    } else {
        launchWithConfig();
    }
    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://bit.ly/CRA-PWA
    serviceWorker.unregister(); // TODO - put this back in once we have dealt with non-React URLs etc and maybe upgraded the service worker code
} else {
    serviceWorker.unregister();
}

// const checkLicence = (activeAppPath: string, configPath: string): boolean => {
//     // Bypass licensing if we are running on "well known" URLs
//     if (
//         !/^https:\/\/[a-zA-Z0-9-.]+\.instantatlas\.com/.test(activeAppPath) &&
//         !/^https:\/\/[a-zA-Z0-9-.]+\.esriuk\.com/.test(activeAppPath) &&
//         activeAppPath !== 'https://ia-report-builder-test.azurewebsites.net' &&
//         activeAppPath !== 'https://esriuk-apps-dev-builds.azurewebsites.net/report-builder' &&
//         activeAppPath !== 'https://reports.localdev:3002' &&
//         activeAppPath !== 'https://reports.localdev' &&
//         activeAppPath !== 'https://localhost:3002'
//     ) {
//         // TODO - migrate this to the Sweet-style licensing...
//         // const pathsList = await fetch(`${configPath}/app.licence`).then((lic) => {
//         //     return lic.text();
//         // });
//         // const paths = pathsList.split('$'),
//         //     activePathHash = Base64.stringify(sha256(`${activeAppPath}/${salt64.SALT}`));
//         // if (paths.indexOf(activePathHash) < 0) {
//         //     console.log(
//         //         `Report Builder for ArcGIS is not licensed for use on site ${activeAppPath}`
//         //     ); // DEBUG for now
//         //     const msg = document.getElementById('appFailMessage'),
//         //         block = document.getElementById('appFailBlock');
//         //     if (msg !== null && block !== null) {
//         //         msg.innerHTML = `Report Builder for ArcGIS is not licensed for use on site ${activeAppPath}. Contact Esri UK support.`;
//         //         block.style.display = 'block';
//         //         return false;
//         //     }
//         //     throw new Error(
//         //         `Report Builder for ArcGIS is not licensed for use on site ${activeAppPath}`
//         //     );
//         // }
//     }
//     return true;
// };
